import React, { useState } from "react";
import styled from "styled-components";
import { useDeviceStatus } from "../hooks/useDeviceStatus";

// Иконка андроида (простая и понятная)
const AndroidIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.6,9.48l1.84-3.18c0.16-0.31,0.04-0.69-0.26-0.85c-0.29-0.15-0.65-0.06-0.83,0.22l-1.88,3.24 c-2.86-1.21-6.08-1.21-8.94,0L5.65,5.67c-0.19-0.29-0.58-0.38-0.87-0.2C4.5,5.65,4.41,6.01,4.56,6.3L6.4,9.48 C3.3,11.25,1.28,14.44,1,18h22C22.72,14.44,20.7,11.25,17.6,9.48z M7,15.25c-0.69,0-1.25-0.56-1.25-1.25 c0-0.69,0.56-1.25,1.25-1.25S8.25,13.31,8.25,14C8.25,14.69,7.69,15.25,7,15.25z M17,15.25c-0.69,0-1.25-0.56-1.25-1.25 c0-0.69,0.56-1.25,1.25-1.25s1.25,0.56,1.25,1.25C18.25,14.69,17.69,15.25,17,15.25z"/>
  </svg>
);

const DeviceButton = styled.button<{
  status: "unbound" | "healthy" | "unhealthy" | "loading";
}>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;

  // Цвета по статусу
  ${(props) => {
    switch (props.status) {
      case "healthy":
        return `
          color: var(--accent-success);
          &:hover {
            background: var(--alert-success-bg);
            color: var(--alert-success-text);
          }
        `;
      case "unhealthy":
        return `
          color: var(--accent-warning);
          &:hover {
            background: var(--alert-warning-bg);
            color: var(--alert-warning-text);
          }
        `;
      case "unbound":
        return `
          color: var(--accent-error);
          &:hover {
            background: var(--alert-error-bg);
            color: var(--alert-error-text);
          }
        `;
      case "loading":
        return `
          color: var(--text-secondary);
          cursor: not-allowed;
          opacity: 0.6;
        `;
      default:
        return `
          color: var(--text-secondary);
        `;
    }
  }}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const LoadingSpinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid var(--border-color);
  border-top: 2px solid var(--text-secondary);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Tooltip = styled.div<{ show: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: var(--bg-tooltip);
  color: var(--text-tooltip);
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  white-space: nowrap;
  pointer-events: none;
  opacity: ${(props) => (props.show ? 1 : 0)};
  visibility: ${(props) => (props.show ? "visible" : "hidden")};
  transition: all 0.2s;
  z-index: 1000;
  margin-bottom: 0.25rem;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: var(--text-primary);
  }
`;

export interface DeviceStatusButtonProps {
  holderId: string;
  onBindDevice: (holderId: string) => void;
  onRebindDevice: (deviceId: string, holderId: string) => void;
  disabled?: boolean;
}

const DeviceStatusButton: React.FC<DeviceStatusButtonProps> = ({
  holderId,
  onBindDevice,
  onRebindDevice,
  disabled = false,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const deviceStatus = useDeviceStatus({
    holderId,
    pollingInterval: 30000, // 30 секунд
    enabled: !disabled,
  });

  const getButtonStatus = () => {
    if (deviceStatus.loading) return "loading";
    if (!deviceStatus.isBound) return "unbound";
    if (deviceStatus.isHealthy) return "healthy";
    return "unhealthy";
  };

  const getTooltipText = () => {
    const status = getButtonStatus();

    switch (status) {
      case "loading":
        return "Загрузка статуса устройства...";
      case "unbound":
        return "Устройство не привязано. Нажмите, чтобы привязать.";
      case "healthy":
        return "Устройство привязано и работает. Нажмите, чтобы перепривязать.";
      case "unhealthy":
        return "Устройство привязано, но есть проблемы. Нажмите для деталей.";
      default:
        return "";
    }
  };

  const handleClick = () => {
    if (disabled || deviceStatus.loading) return;

    const status = getButtonStatus();

    if (status === "unbound") {
      onBindDevice(holderId);
    } else if (status === "healthy" || status === "unhealthy") {
      if (deviceStatus.deviceId) {
        onRebindDevice(deviceStatus.deviceId, holderId);
      }
    }
  };

  const renderIcon = () => {
    const status = getButtonStatus();

    if (status === "loading") {
      return <LoadingSpinner />;
    }
    
    return <AndroidIcon />;
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <DeviceButton
        status={getButtonStatus()}
        onClick={handleClick}
        disabled={disabled || deviceStatus.loading}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        title={getTooltipText()}
      >
        {renderIcon()}
      </DeviceButton>

      <Tooltip show={showTooltip && !disabled}>{getTooltipText()}</Tooltip>
    </div>
  );
};

export default DeviceStatusButton;
